import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";
import { t } from "../lib/i18n";

import TournamentAdmin from "./admin/TournamentAdmin";
import ApprovalAdmin from "./admin/ApprovalAdmin";
import PlayersAdmin from "./admin/PlayersAdmin";
import DecksAdmin from "./admin/DecksAdmin";
import ComponentsAdmin from "./admin/ComponentsAdmin";
import UsersAdmin from "./admin/UsersAdmin";
import SiteSettingsAdmin from "./admin/SiteSettingsAdmin";
import CreatorsAdmin from "./admin/CreatorsAdmin";

export default function AdminDashboard() {
  const { admin } = useAuth();
  const [tab, setTab] = useState("tournaments");
  const [tournaments, setTournaments] = useState([]);
  const [players, setPlayers] = useState([]);
  const [decks, setDecks] = useState([]);
  const [components, setComponents] = useState([]);
  const [matches, setMatches] = useState([]);
  const [activeTour, setActiveTour] = useState(null);

  const refresh = async () => {
    const [tt, p, d, c] = await Promise.all([
      api.get("/tournaments"),
      api.get("/players"),
      api.get("/decks"),
      api.get("/components"),
    ]);
    setTournaments(tt.data);
    setPlayers(p.data);
    setDecks(d.data);
    setComponents(c.data);
  };

  useEffect(() => {
    if (admin && admin.role === "admin") refresh();
  }, [admin]);

  useEffect(() => {
    if (activeTour) {
      api.get(`/tournaments/${activeTour}`).then((r) => setMatches(r.data.matches));
    }
  }, [activeTour]);

  if (admin === null) return <div className="p-10 font-mono text-white/40">{t.loading}</div>;
  if (!admin || admin.role !== "admin") return <Navigate to="/admin/login" replace />;

  return (
    <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10">
      <div className="border-b border-white/10 pb-4 flex items-end justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-[#FF0055] font-mono">
            // {t.controlRoomEyebrow}
          </div>
          <h1 className="font-display font-black uppercase italic text-5xl mt-2">{t.adminDashboard}</h1>
        </div>
        <span className="text-[10px] uppercase tracking-[0.25em] font-mono text-[#00E5FF]">
          {admin.email}
        </span>
      </div>

      <div className="border-b border-white/10 flex gap-1 mt-6 overflow-x-auto">
        {[
          { id: "tournaments", label: t.tabTournaments },
          { id: "approval", label: t.tabApproval },
          { id: "players", label: t.tabPlayers },
          { id: "decks", label: t.tabDecks },
          { id: "components", label: t.tabComponents },
          { id: "users", label: t.tabUsers },
          { id: "creators", label: t.tabCreators },
          { id: "settings", label: t.tabSettings },
        ].map((x) => (
          <button
            key={x.id}
            onClick={() => setTab(x.id)}
            data-testid={`admin-tab-${x.id}`}
            className={`px-5 py-3 text-xs uppercase tracking-[0.25em] font-bold whitespace-nowrap border-b-2 -mb-px ${
              tab === x.id ? "text-[#00E5FF] border-[#00E5FF]" : "text-white/50 border-transparent hover:text-white"
            }`}
          >
            {x.label}
          </button>
        ))}
      </div>

      {tab === "tournaments" && (
        <TournamentAdmin
          tournaments={tournaments}
          players={players}
          activeTour={activeTour}
          setActiveTour={setActiveTour}
          matches={matches}
          refresh={refresh}
          loadMatches={() =>
            activeTour && api.get(`/tournaments/${activeTour}`).then((r) => setMatches(r.data.matches))
          }
        />
      )}
      {tab === "approval" && <ApprovalAdmin players={players} refresh={refresh} />}
      {tab === "players" && <PlayersAdmin players={players} refresh={refresh} />}
      {tab === "decks" && <DecksAdmin decks={decks} components={components} players={players} refresh={refresh} />}
      {tab === "components" && <ComponentsAdmin components={components} refresh={refresh} />}
      {tab === "users" && <UsersAdmin />}
      {tab === "creators" && <CreatorsAdmin />}
      {tab === "settings" && <SiteSettingsAdmin />}
    </div>
  );
}
